import React, { useEffect, useState } from 'react';
import { Box, Typography, Card, CardContent, Chip, List, ListItem, ListItemText, Stack } from '@mui/material';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { apiService } from '../services/apiService';

const Profile: React.FC = () => {
  const { handle } = useParams<{ handle: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<any | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        if (!handle) { setError('No handle provided'); setLoading(false); return; }
        const resp = await apiService.getPublicProfile(handle);
        if (cancelled) return;
        setData(resp.data || null);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.response?.data?.detail || 'Profile not found');
        setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [handle]);

  if (loading) return <Typography color="textSecondary">Loading profile…</Typography>;
  if (error) return <Typography color="error">{error}</Typography>;
  if (!data) return <Typography color="textSecondary">Profile not found.</Typography>;

  const profile = data.profile || {};
  const stats = data.stats || {};
  const recent = Array.isArray(data.recent_follows) ? data.recent_follows : [];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>@{profile.handle}</Typography>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Chip label={profile.visibility === 'public' ? 'Public' : 'Private'} color={profile.visibility === 'public' ? 'success' : 'default'} size="small" />
      </Stack>
      {profile.bio && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>Bio</Typography>
            <Typography variant="body1" whiteSpace="pre-wrap">{profile.bio}</Typography>
          </CardContent>
        </Card>
      )}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Stats</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
            <Box>
              <Typography variant="overline" color="textSecondary">Follows</Typography>
              <Typography variant="h5">{stats.follows ?? 0}</Typography>
            </Box>
            <Box>
              <Typography variant="overline" color="textSecondary">Bookmarks</Typography>
              <Typography variant="h5">{stats.bookmarks ?? 0}</Typography>
            </Box>
            <Box>
              <Typography variant="overline" color="textSecondary">Comments</Typography>
              <Typography variant="h6">{stats.comments ?? 0}</Typography>
            </Box>
            <Box>
              <Typography variant="overline" color="textSecondary">Reactions</Typography>
              <Typography variant="h6">{stats.reactions ?? 0}</Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Recently Followed Bills</Typography>
          {recent.length === 0 ? (
            <Typography color="textSecondary">No recent follows.</Typography>
          ) : (
            <List>
              {recent.map((b: any, idx: number) => (
                <ListItem key={idx} divider component={RouterLink as any} to={b.id ? `/bills/${b.id}` : '#'}>
                  <ListItemText primary={b.title || b.id} secondary={b.latest_action_text || b.latest_action_date || ''} />
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Profile;
